/**
 * useKeyboardHeight — visualViewport-based keyboard height hook.
 *
 * Returns the on-screen keyboard height in pixels (0 when keyboard is closed).
 *
 * iOS 26 trap (Apple Forum 800125): when the keyboard is *closing*, the browser
 * briefly fires a resize event with offsetTop > 0 (the viewport has shifted down
 * before it is restored). Applying that value causes a visible layout jump. We
 * skip the update whenever offsetTop > 0 to preserve the previous height.
 *
 * Feature detection: window.visualViewport may be absent (server rendering,
 * old browsers). Returns 0 immediately if the API is unavailable.
 *
 * ADR-023: window.visualViewport is a platform-neutral DOM API — allowed
 * unconditionally (no PlatformAdapter routing needed).
 */

import { useState, useEffect } from 'react';

export function useKeyboardHeight(): number {
  const [keyboardHeight, setKeyboardHeight] = useState<number>(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) {
      // API absent — stay at 0, no listener to register.
      return;
    }

    function handleResize() {
      // iOS 26 trap: offsetTop > 0 means the viewport shifted down while the
      // keyboard is closing. Skip this transient state to avoid layout jump.
      if (vv!.offsetTop > 0) {
        return;
      }

      // Keyboard height = total window height minus the visible viewport height
      // minus the viewport's vertical offset from the top.
      const height = window.innerHeight - vv!.height - vv!.offsetTop;
      setKeyboardHeight(Math.max(0, height));
    }

    vv.addEventListener('resize', handleResize);
    return () => {
      vv.removeEventListener('resize', handleResize);
    };
  }, []);

  return keyboardHeight;
}
